// Browse in Shopify collection context while keeping the existing search hero intact.
class RaveyardSearchCatalog extends HTMLElement {
  #request;
  #retry;

  connectedCallback() {
    this.addEventListener('click', this.#click);
    this.addEventListener('submit', this.#submit);
    window.addEventListener('popstate', this.#restore);
    const params = new URL(window.location.href).searchParams;
    if (params.has('catalog_sort') || params.has('catalog_page')) this.#restore();
  }

  disconnectedCallback() {
    this.#request?.abort();
    this.removeEventListener('click', this.#click);
    this.removeEventListener('submit', this.#submit);
    window.removeEventListener('popstate', this.#restore);
  }

  #collectionUrl(value) {
    const url = new URL(value, window.location.origin);
    const root = new URL(this.dataset.defaultCollectionUrl, window.location.origin);
    if (url.origin !== root.origin || url.pathname !== root.pathname) return null;
    return url;
  }

  #click = (event) => {
    if (event.target.closest('[data-ry-catalog-retry]')) {
      if (this.#retry) this.#load(this.#retry.url, this.#retry.options);
      return;
    }
    const link = event.target.closest('a[data-ry-catalog-link]');
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const url = this.#collectionUrl(link.href);
    if (!url) return;
    event.preventDefault();
    this.#load(url, {history: true, focus: link.hasAttribute('data-ry-catalog-page') ? 'grid' : 'sort'});
  };

  #submit = (event) => {
    const form = event.target.closest('form[data-ry-catalog-sort]');
    if (!form) return;
    const url = this.#collectionUrl(form.action);
    if (!url) return;
    url.search = new URLSearchParams(new FormData(form)).toString();
    event.preventDefault();
    this.#load(url, {history: true, focus: 'sort'});
  };

  #restore = () => {
    const state = new URL(window.location.href);
    if (state.pathname !== new URL(this.dataset.searchUrl, state.origin).pathname || state.searchParams.has('q')) return;
    const url = new URL(this.dataset.defaultCollectionUrl, state.origin);
    const target = this.#collectionUrl(url);
    if (!target) return;
    if (state.searchParams.has('catalog_sort')) target.searchParams.set('sort_by', state.searchParams.get('catalog_sort'));
    if (state.searchParams.has('catalog_page')) target.searchParams.set('page', state.searchParams.get('catalog_page'));
    this.#load(target, {history: false});
  };

  async #load(url, options) {
    this.#request?.abort();
    const request = new AbortController();
    this.#request = request;
    this.#retry = {url, options};
    this.removeAttribute('data-load-failed');
    const errorPanel = this.querySelector('[data-ry-catalog-error]');
    if (errorPanel) errorPanel.hidden = true;
    this.setAttribute('aria-busy', 'true');
    const status = this.querySelector('[data-ry-catalog-status]');
    if (status) status.textContent = 'Loading products…';
    const endpoint = new URL(url);
    endpoint.searchParams.set('section_id', this.dataset.sectionId);
    try {
      const response = await fetch(endpoint, {signal: request.signal});
      if (!response.ok) throw new Error('Catalog section unavailable');
      const document = new DOMParser().parseFromString(await response.text(), 'text/html');
      const incoming = document.querySelector('ry-search-catalog');
      const content = incoming?.querySelector('[data-ry-catalog-content]');
      if (!content || !this.#collectionUrl(incoming.dataset.collectionUrl) || request.signal.aborted) throw new Error('Catalog section missing');
      this.querySelector('[data-ry-catalog-content]').replaceWith(content);
      this.dataset.collectionUrl = incoming.dataset.collectionUrl;
      this.dispatchEvent(new CustomEvent('shopify:section:load', {bubbles: true}));
      if (options.history) {
        const state = new URL(window.location.href);
        for (const [key, source] of [['catalog_sort', 'sort_by'], ['catalog_page', 'page']]) {
          if (url.searchParams.has(source)) state.searchParams.set(key, url.searchParams.get(source));
          else state.searchParams.delete(key);
        }
        window.history.pushState(null, '', state);
      }
      const updatedStatus = this.querySelector('[data-ry-catalog-status]');
      const grid = this.querySelector('[data-ry-catalog-grid]');
      if (updatedStatus && grid) updatedStatus.textContent = `Page ${grid.dataset.catalogPage} of ${grid.dataset.catalogPages}`;
      if (this.#request === request) this.removeAttribute('aria-busy');
      if (options.focus) this.querySelector(options.focus === 'grid' ? '[data-ry-catalog-grid]' : 'select[name="sort_by"]')?.focus({preventScroll: options.focus !== 'grid'});
    } catch (error) {
      if (!request.signal.aborted) {
        this.setAttribute('data-load-failed', '');
        if (status) status.textContent = 'Products could not load. Retry or open All Products.';
        if (errorPanel) errorPanel.hidden = false;
        const fallback = this.querySelector('[data-ry-catalog-fallback]');
        if (fallback) fallback.href = url.toString();
      }
    } finally {
      if (this.#request === request) this.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('ry-search-catalog')) customElements.define('ry-search-catalog', RaveyardSearchCatalog);
